<script setup lang="ts">
import { onUnmounted } from 'vue';

import KBadge from '@/components/badges/KBadge.vue';
import KBtn from '@/components/btns/KBtn.vue';
import DispatchDriverSearchForm from '@/components/searchForms/DispatchDriverSearchForm.vue';
import DispatchJobSearchForm from '@/components/searchForms/DispatchJobSearchForm.vue';
import SmartDispatchJobDetailsSection from '@/components/sections/SmartDispatchJobDetailsSection.vue';
import SmartDispatchStopDetailsSection from '@/components/sections/SmartDispatchStopDetailsSection.vue';
import DispatchDriversTable from '@/components/tables/DispatchDriversTable.vue';
import DispatchJobsTable from '@/components/tables/DispatchJobsTable.vue';
import SmartDispatchJobsTabs from '@/components/tabs/SmartDispatchJobsTabs.vue';
import SmartDispatchVWindow from '@/components/vWindows/SmartDispatchVWindow.vue';
import DispatchSectionWrapper from '@/components/wrappers/DispatchSectionWrapper.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import useDispatchDashboardViewData from '@/composables/data/useDispatchDashboardViewData';
import useDispatchRouteQuery from '@/composables/routeQueries/useDispatchRouteQuery';
import useDispatchDashboardViewWS from '@/composables/ws/useDispatchDashboardViewWS';
import { useDispatchDashboardViewStore } from '@/stores/useDispatchDashboardViewStore';
import * as dateHelpers from '@/helpers/date';

const routeQuery = useDispatchRouteQuery({
  date: dateHelpers.getApiZonedEndOfTodayTZDate(),
  tab: 'unallocated',
});

const dispatchDashboardViewStore = useDispatchDashboardViewStore();
dispatchDashboardViewStore.init(
  useDispatchDashboardViewData(routeQuery, useDispatchDashboardViewWS()),
  routeQuery,
);

onUnmounted(function () {
  dispatchDashboardViewStore.$reset();
});
</script>

<template>
  <div
    :class="$style.dispatchDashboardView"
    data-testid="dispatchDashboardView"
  >
    <PageContentWrapper>
      <div :class="[$style.firstWidthWrapper, $style.tabsFirstWidthWrapper]">
        <div :class="$style.leftColumn" class="d-flex">
          <SmartDispatchJobsTabs :class="$style.jobsTabs" />
        </div>
        <div :class="$style.rightColumn">
          <h3 class="m-0">
            Drivers ({{ dispatchDashboardViewStore.driverCount }})
          </h3>
        </div>
      </div>
      <div :class="[$style.firstWidthWrapper, $style.searchFormsWidthWrapper]">
        <div :class="$style.leftColumn">
          <FormWrapper :class="$style.formWrapper">
            <DispatchJobSearchForm
              :initial-data="
                dispatchDashboardViewStore.dispatchJobSearchFormInitialData
              "
              :loading="dispatchDashboardViewStore.data.isFetchingDispatchJobs"
              :class="$style.form"
              @submit="dispatchDashboardViewStore.jobSearch"
            />
          </FormWrapper>
        </div>
        <div :class="$style.rightColumn">
          <FormWrapper :class="$style.formWrapper">
            <DispatchDriverSearchForm
              :initial-data="
                dispatchDashboardViewStore.dispatchDriverSearchFormInitialData
              "
              :class="$style.form"
              @submit="dispatchDashboardViewStore.driverSearch"
            />
          </FormWrapper>
        </div>
      </div>
      <div :class="$style.firstWidthWrapper">
        <div :class="$style.leftColumn">
          <div :class="$style.jobTableWrapper">
            <KBadge
              v-if="
                dispatchDashboardViewStore.selectedJobs?.length &&
                !dispatchDashboardViewStore.data.isFetchingDispatchJobs
              "
              :class="$style.jobsTableCountBadge"
              :content="dispatchDashboardViewStore.selectedJobs.length"
              inline
            />
            <SmartDispatchVWindow :class="$style.windowItems">
              <template #default="{ tabValue, tabKey, tabIndex }">
                <DispatchJobsTable
                  v-if="
                    tabKey ===
                    dispatchDashboardViewStore.query.tab /* unmounting */
                  "
                  v-model="dispatchDashboardViewStore.jobsTableModel"
                  :class="[
                    $style.table,
                    {
                      [$style.noData]:
                        dispatchDashboardViewStore.data.isFetchingDispatchJobs,
                    },
                  ]"
                  :data="dispatchDashboardViewStore.dispatchJobsTableData"
                  :loading="
                    dispatchDashboardViewStore.data.isFetchingDispatchJobs
                  "
                />
              </template>
            </SmartDispatchVWindow>
          </div>
          <div :class="$style.secondWidthWrapper">
            <DispatchSectionWrapper
              :class="[
                $style.dispatchSectionWrapper,
                $style.jobDetailsSectionWrapper,
              ]"
            >
              <template #header>
                <div class="flex items-center justify-between">
                  <h3 class="m-0">
                    Job
                    <template
                      v-if="
                        !Number.isNaN(dispatchDashboardViewStore.activatedJobId)
                      "
                    >
                      #{{ dispatchDashboardViewStore.activatedJobId }}
                    </template>
                  </h3>
                  <div class="mr-n4">
                    <KBtn
                      variant="text"
                      append-icon="mdi-pencil-outline"
                      size="md"
                      :class="$style.dispatchSectionHeaderBtn"
                      :disabled="
                        Number.isNaN(dispatchDashboardViewStore.activatedJobId)
                      "
                      @click="dispatchDashboardViewStore.handleEditJobClick"
                    >
                      Edit Job
                    </KBtn>
                  </div>
                </div>
              </template>
              <SmartDispatchJobDetailsSection />
            </DispatchSectionWrapper>
            <DispatchSectionWrapper
              :class="[
                $style.dispatchSectionWrapper,
                $style.stopDetailsSectionWrapper,
              ]"
            >
              <template #header>
                <h3 class="m-0">Stop Details</h3>
              </template>
              <SmartDispatchStopDetailsSection />
            </DispatchSectionWrapper>
          </div>
        </div>
        <div :class="$style.rightColumn">
          <div :class="$style.driverTableWrapper">
            <DispatchDriversTable
              :class="$style.table"
              :data="dispatchDashboardViewStore.dispatchDriversTableData"
            />
          </div>
        </div>
      </div>
    </PageContentWrapper>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

$jobTableAddHeight: -557px;
$driverTableAddHeight: -259px;

.dispatchDashboardView {
}
  .firstWidthWrapper {
    display: flex;
    gap: 1.5rem;
  }
  .tabsFirstWidthWrapper {
    margin-top: -1rem;
    align-items: center;
  }
  .searchFormsWidthWrapper {
    margin-top: 0.5rem;
  }
    .leftColumn {
      width: 100%;
      min-width: 850px;
      max-width: 1000px;
      position: relative;
    }
      .jobsTabs {
      }
      .windowItems {
        overflow: visible;
      }
    .rightColumn {
      width: 60%;
      min-width: 475px;
      max-width: 600px;
    }
      .jobTableWrapper {
        /* height: calc(100vh + $jobTableAddHeight); */
        height: 456px;
        overflow-y: scroll; /* TODO maybe use custom scrollbar library because of the differences in ff and chrome */
        overflow-x: hidden;
        background-color: colors.$color-white;

        .table {
          &.noData {
            height: 100%;
          }
        }
      }
        .jobsTableCountBadge {
          position: absolute;
          z-index: 2;
          right: -13px;
          top: -10px;
        }
      .driverTableWrapper {
        min-height: calc(100vh + $driverTableAddHeight - 1.5rem);
        background-color: colors.$color-white;
      }
      .formWrapper {
        padding: calc(16px - 0.5rem) 16px;
        margin: 0 0 1.5rem 0;
      }
      .secondWidthWrapper {
        display: flex;
        gap: 1.5rem;
        margin: 1.5rem 0 0 0 ;
      }
        .dispatchSectionWrapper {
          height: 100%;
        }
        .jobDetailsSectionWrapper {
          width: 50%;
        }
        .stopDetailsSectionWrapper {
          width: 50%;
        }
          .dispatchSectionHeaderBtn {
            text-transform: none;
          }
</style>
